import { HexLocator, getHexId, hexIdToHumanHexId } from "@hex/common";
import { useEffect } from "react";
import { shallowEqual } from "react-redux";
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useHexSelector } from "../../../hex-version-multiplayer/state-hooks/hexStateHooks";
import { HexMP } from "../../../redux/slices/hexMPSlice";
import { useHexFlag } from "../../../util/useHexFlags";
import {
  ClientSideRoute,
  HexRoutePathParams,
  useHexRoutePathParams,
} from "../../routes";

const useHumanHexId = (): HexLocator => {
  const { hexId, title } = useHexSelector({
    selector: (hex: HexMP) => ({
      hexId: hex.id,
      title: hex.title,
    }),
    equalityFn: shallowEqual,
  });

  return hexIdToHumanHexId(getHexId(hexId), title);
};

export const useTargetHexLocator = (): HexLocator => {
  const humanHexId = useHumanHexId();
  const humanTitlesInUrlEnabled = useHexFlag("human-titles-in-url");
  const hexLocator = humanTitlesInUrlEnabled
    ? humanHexId
    : getHexId(humanHexId);
  // Temp type cast while flagged, as we could still fall back to hexId
  return hexLocator as unknown as HexLocator;
};

interface HumanHexUrlProps {
  route: ClientSideRoute<string, HexRoutePathParams>;
}

export const HumanHexUrl: React.FC<HumanHexUrlProps> = ({ route }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hexLocator } = useHexRoutePathParams();

  const targetHexLocator = useTargetHexLocator();

  useEffect(() => {
    if (route.clientSide) {
      // Use the route's path pattern to ensure we're only doing this redirect for the route passed in as an extra layer of security
      const match = matchPath(
        {
          path: route.path,
          end: true,
        },
        location.pathname,
      );

      if (match) {
        // Only replace if the hexLocator format is different
        if (hexLocator !== targetHexLocator) {
          // Use generatePath to structurally replace the hexLocator parameter
          const newPathname = generatePath(route.path, {
            ...match.params,
            hexLocator: targetHexLocator,
          });

          navigate(
            {
              pathname: newPathname,
              search: location.search,
              hash: location.hash,
            },
            { replace: true },
          );
        }
      }
    }
  }, [hexLocator, route, location, targetHexLocator, navigate]);

  return null;
};
